import React from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Section, Toggle } from '../settingsPrimitives';
import { usePreferencesStore } from '../../store/preferencesStore';
import {
  clampLongMessageKb,
  MIN_LONG_MESSAGE_KB,
  MAX_LONG_MESSAGE_KB,
  SERVER_MAX_BODY_BYTES,
} from '../../utils/chatComposeLimits';

/**
 * Chat extension settings, shown under Settings → Extensions → Chat.
 *
 * Everything here is about ONE fact: the server's message limit is counted in
 * UTF-8 BYTES, not characters (`byte_size(body)` vs `@max_body_bytes`). For an
 * English writer those numbers coincide and none of this is visible. For a
 * Russian writer the cap arrives at roughly a third of the characters, and for
 * a Japanese writer a fifth — which is why the symptom was reported as "chat
 * does not support UTF-8" rather than as a length limit.
 *
 * There is deliberately NO encoding picker. UTF-8 is not a preference in this
 * app, it is the only encoding in the path: Postgres stores `text`, Elixir
 * carries UTF-8 binaries, JSON is UTF-8 by spec, and `new Blob([string])`
 * encodes as UTF-8 with no alternative. A dropdown listing UTF-8 next to
 * Latin-1 would imply a choice the stack cannot honour, and a knob that does
 * nothing is worse than no knob. The read-only row below says so out loud.
 */
export default function ChatExtensionSettings() {
  const { t } = useTranslation();
  const asFile = usePreferencesStore((s) => s.chatLongMessageAsFile);
  const kb = usePreferencesStore((s) => s.chatLongMessageKb);
  const showIndicator = usePreferencesStore((s) => s.chatShowSizeIndicator);
  const update = usePreferencesStore((s) => s.updatePreference);

  return (
    <>
      <Section title={t('chatExt.settingsEncoding', 'Encoding')}>
        <Row
          label={t('chatExt.encoding', 'Text encoding')}
          desc={t(
            'chatExt.encodingDesc',
            'Messages, code blocks and text attachments are UTF-8 everywhere — in the app, on the wire and in the database. There is nothing to switch, so every script and emoji is supported as sent. The one consequence worth knowing: the size limit below counts bytes, and non-Latin scripts use 2–4 bytes per character.'
          )}
        >
          <span className="settings-static-value">UTF-8</span>
        </Row>
      </Section>

      <Section title={t('chatExt.settingsComposing', 'Composing')}>
        <Row
          label={t('chatExt.longMessageAsFile', 'Send long messages as a file')}
          desc={t(
            'chatExt.longMessageAsFileDesc',
            'A message over the size limit is attached as a .txt file instead of being rejected, so nothing you wrote is lost. Turn this off and the server refuses the message.'
          )}
        >
          <Toggle value={asFile} onChange={(v) => update('chatLongMessageAsFile', v)} />
        </Row>

        <Row
          label={t('chatExt.longMessageKb', 'Size limit')}
          desc={t('chatExt.longMessageKbDesc', {
            defaultValue:
              'Kilobytes of UTF-8 text before a message becomes a .txt attachment ({{min}}–{{max}}). The server rejects anything over {{cap}} KB, so leaving headroom protects against a reply quote pushing a borderline message over.',
            min: MIN_LONG_MESSAGE_KB,
            max: MAX_LONG_MESSAGE_KB,
            cap: SERVER_MAX_BODY_BYTES / 1000,
          })}
        >
          <div className="settings-input-group">
            <input
              type="number"
              min={MIN_LONG_MESSAGE_KB}
              max={MAX_LONG_MESSAGE_KB}
              value={kb}
              // Raw while typing (the composer clamps on read), normalized on
              // blur — so clearing the field to retype does not momentarily
              // persist a threshold of 1 KB and start filing short messages.
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                if (!Number.isNaN(n)) update('chatLongMessageKb', n);
              }}
              onBlur={(e) => update('chatLongMessageKb', clampLongMessageKb(e.target.value))}
              className="settings-number-input"
              style={{ width: 64 }}
              disabled={!asFile}
            />
            <span className="settings-unit-label">KB</span>
          </div>
        </Row>

        <Row
          label={t('chatExt.showSizeIndicator', 'Show size indicator')}
          desc={t(
            'chatExt.showSizeIndicatorDesc',
            'Display how much of the size limit the message uses, once past three quarters. Worth leaving on for non-Latin scripts, where the character count on screen is a poor guide to the byte count.'
          )}
        >
          <Toggle value={showIndicator} onChange={(v) => update('chatShowSizeIndicator', v)} />
        </Row>
      </Section>
    </>
  );
}
